import type { AttendeeRow, CallSheetDoc, CrewRow, TalentRow } from './types';

export type Section =
  | 'project'
  | 'crew'
  | 'schedule'
  | 'shots'
  | 'locations'
  | 'talent'
  | 'questions'
  | 'safety'
  | 'attendees'
  | 'equipment'
  | 'specs'
  | 'clientExtras'
  | 'notes'
  | 'internal';

export const SECTIONS: { id: Section; label: string; adminOnly?: boolean }[] = [
  { id: 'project', label: 'Project' },
  { id: 'crew', label: 'Crew' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'shots', label: 'Shots' },
  { id: 'locations', label: 'Locations' },
  { id: 'talent', label: 'Talent' },
  { id: 'questions', label: 'Questions' },
  { id: 'safety', label: 'Safety' },
  { id: 'attendees', label: 'Attendees' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'specs', label: 'Specs' },
  { id: 'clientExtras', label: 'Client extras' },
  { id: 'notes', label: 'Notes' },
  { id: 'internal', label: 'Internal', adminOnly: true },
];

// Sections kept in client_schedule mode. The same list is also the always-
// visible base for full call sheets that explicitly opt into extra sections.
export const CLIENT_SCHEDULE_SECTIONS: Section[] = [
  'project',
  'schedule',
  'shots',
  'locations',
  'clientExtras',
  'notes',
  'internal',
];

/**
 * Resolve the structural sections rendered for a document.
 *
 * - client_schedule always uses its fixed simplified section set;
 * - callsheet with enabledSections uses the client set plus enabled extras;
 * - legacy callsheet without enabledSections retains all 14 sections.
 *
 * The result follows SECTIONS order regardless of the persisted enable order.
 */
export function visibleSections(doc: Pick<CallSheetDoc, 'docType' | 'enabledSections'>): Section[] {
  if (doc.docType === 'client_schedule') return [...CLIENT_SCHEDULE_SECTIONS];
  if (doc.enabledSections === undefined) return SECTIONS.map((section) => section.id);

  const visible = new Set<Section>([...CLIENT_SCHEDULE_SECTIONS, ...doc.enabledSections]);
  return SECTIONS.map((section) => section.id).filter((id) => visible.has(id));
}

type Shape = Pick<CallSheetDoc, 'docType' | 'enabledSections'>;

/** People rows that are on the sheet. A hidden (or never-added) section keeps
 *  its rows in the doc, but nobody in it is shown, emailed, chased or offered
 *  a role view. Every people consumer goes through these three. */
export function crewOnSheet(doc: Shape & { crew?: CrewRow[] }): CrewRow[] {
  return visibleSections(doc).includes('crew') ? (doc.crew ?? []) : [];
}

export function talentOnSheet(doc: Shape & { talent?: TalentRow[] }): TalentRow[] {
  return visibleSections(doc).includes('talent') ? (doc.talent ?? []) : [];
}

export function attendeesOnSheet(doc: Shape & { attendees?: AttendeeRow[] }): AttendeeRow[] {
  return visibleSections(doc).includes('attendees') ? (doc.attendees ?? []) : [];
}
